import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Tooltip,
  MenuItem,
  TableSortLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { ApiClient } from '../services/api';
import type { AdminUser } from '../services/api';

// Role display labels. Keys are Firebase custom-claim values and MUST NOT change
// — only the human-readable labels below. Course Mapper (not yet built) and the
// duplicative User Management role were removed from assignment (SOPHIA-34852).
// Array order is the display order mandated by SOPHIA-34854.
const VALID_ROLES = [
  { key: 'sophia_admin', label: 'Sophia Administrator' },
  { key: 'sophia_partner_admin', label: 'Partner Success Admin - Full' },
  { key: 'partner', label: 'Partner Success Admin - Limited' },
  { key: 'transcript_viewer', label: 'Transcript Viewer' },
  { key: 'academic_advisor', label: 'Student Summary Viewer' },
  { key: 'api_developer', label: 'API Developer' },
  { key: 'bucket_management', label: 'Transcript Storage Manager' },
  { key: 'client_admin', label: 'Partner Institution Admin' },
] as const;

// Lookup for rendering role chips with the same labels as the assignment UI.
const ROLE_LABEL_MAP: Record<string, string> = Object.fromEntries(
  VALID_ROLES.map((r) => [r.key, r.label])
);

// Internal Sophia staff roles are provisioned WITHOUT a partnerId claim
// (SOPHIA-35095): they scope to a partner via the switcher and stay out of
// partner user lists. Mirrors the backend's INTERNAL_ROLES.
const INTERNAL_ROLES = ['sophia_admin', 'sophia_partner_admin', 'partner'];

function requiresPartnerId(roles: string[]): boolean {
  return roles.length > 0 && !roles.every((r) => INTERNAL_ROLES.includes(r));
}

const PRESETS: Record<string, string[]> = {
  All: [
    'sophia_admin',
    'partner',
    'transcript_viewer',
    'api_developer',
    'academic_advisor',
    'bucket_management',
  ],
  Admin: ['sophia_admin'],
  Advisor: ['academic_advisor', 'transcript_viewer'],
  API: ['api_developer', 'transcript_viewer'],
  Viewer: ['transcript_viewer'],
  None: [],
};

type SortColumn = 'email' | 'displayName' | 'partner' | 'lastActive';
type SortOrder = 'asc' | 'desc';

/** Partner-filter value for users without a partnerId claim (internal staff). */
export const NO_PARTNER = 'none';

function getPartnerId(user: AdminUser): string | null {
  const pid = user.customClaims?.partnerId;
  const str = pid != null ? String(pid).trim() : '';
  return str !== '' ? str : null;
}

/**
 * Client-side search + partner filter + column sort over the full user list
 * (SOPHIA-35249). Null/missing sort values always sort last so partnerless
 * internal users and never-active accounts don't crowd the top.
 */
export function filterAndSortUsers(
  users: AdminUser[],
  opts: {
    search: string;
    partnerFilter: string; // '' = all, NO_PARTNER, or a partner id
    sortColumn: SortColumn;
    sortOrder: SortOrder;
    partnerNames: Map<string, string>;
  }
): AdminUser[] {
  const search = opts.search.trim().toLowerCase();

  let result = users;
  if (search) {
    result = result.filter(
      (u) =>
        u.email?.toLowerCase().includes(search) || u.displayName?.toLowerCase().includes(search)
    );
  }
  if (opts.partnerFilter) {
    result = result.filter((u) =>
      opts.partnerFilter === NO_PARTNER
        ? getPartnerId(u) === null
        : getPartnerId(u) === opts.partnerFilter
    );
  }

  const sortValue = (u: AdminUser): string | number | null => {
    switch (opts.sortColumn) {
      case 'email':
        return u.email?.toLowerCase() ?? null;
      case 'displayName':
        return u.displayName?.toLowerCase() ?? null;
      case 'partner': {
        const pid = getPartnerId(u);
        if (pid === null) return null;
        return (opts.partnerNames.get(pid) ?? pid).toLowerCase();
      }
      case 'lastActive': {
        const time = u.lastRefreshTime ? Date.parse(u.lastRefreshTime) : NaN;
        return Number.isNaN(time) ? null : time;
      }
    }
  };

  const direction = opts.sortOrder === 'asc' ? 1 : -1;
  return [...result].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    if (va === null && vb === null) return 0;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (va < vb) return -1 * direction;
    if (va > vb) return 1 * direction;
    return 0;
  });
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function RoleChips({ claims }: { claims: Record<string, unknown> }) {
  const roles = Array.isArray(claims.roles) ? (claims.roles as string[]) : [];
  if (roles.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No roles
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {roles.map((role) => (
        <Chip
          key={role}
          label={ROLE_LABEL_MAP[role] ?? role.replace(/_/g, ' ')}
          size="small"
          sx={{ fontSize: '0.7rem', height: 22 }}
        />
      ))}
    </Stack>
  );
}

export function UserManagement(): React.ReactElement {
  const { idToken } = useAuth();
  const { partners } = usePartnerScope();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('email');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Edit modal state
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [editPartnerId, setEditPartnerId] = useState('');
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Success snackbar
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Create user modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createFirstName, setCreateFirstName] = useState('');
  const [createLastName, setCreateLastName] = useState('');
  const [createPartnerId, setCreatePartnerId] = useState('');
  const [createRoles, setCreateRoles] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Remove user confirmation
  const [removeUser, setRemoveUser] = useState<AdminUser | null>(null);
  const [removing, setRemoving] = useState(false);

  // Disable/enable in-progress tracking
  const [togglingUid, setTogglingUid] = useState<string | null>(null);

  const client = useMemo(() => new ApiClient(idToken), [idToken]);

  const fetchUsers = useCallback(async () => {
    if (!idToken) return;
    try {
      setLoading(true);
      setError(null);
      const data = await client.getAdminUsers();
      setUsers(data.users);
    } catch (err) {
      console.error('[UserManagement] Failed to fetch users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [idToken, client]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const partnerNames = useMemo(
    () => new Map(partners.map((p) => [String(p.id), p.name])),
    [partners]
  );

  // Explicit false only — a missing flag must never brand a partner inactive
  const inactivePartnerIds = useMemo(
    () => new Set(partners.filter((p) => p.active === false).map((p) => String(p.id))),
    [partners]
  );

  // Filter options are the partners actually present in the user list
  const partnerOptions = useMemo(() => {
    const ids = new Set<string>();
    let hasPartnerless = false;
    for (const u of users) {
      const pid = getPartnerId(u);
      if (pid === null) hasPartnerless = true;
      else ids.add(pid);
    }
    const options = [...ids]
      .map((id) => ({
        id,
        name: partnerNames.get(id) ?? null,
        inactive: inactivePartnerIds.has(id),
      }))
      .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id, undefined, { numeric: true }));
    return { options, hasPartnerless };
  }, [users, partnerNames, inactivePartnerIds]);

  const displayedUsers = useMemo(
    () =>
      filterAndSortUsers(users, {
        search: searchInput,
        partnerFilter,
        sortColumn,
        sortOrder,
        partnerNames,
      }),
    [users, searchInput, partnerFilter, sortColumn, sortOrder, partnerNames]
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // Open edit modal
  const openEditModal = (user: AdminUser) => {
    setEditUser(user);
    const claims = user.customClaims ?? {};
    setEditPartnerId(claims.partnerId != null ? String(claims.partnerId) : '');
    setEditRoles(Array.isArray(claims.roles) ? (claims.roles as string[]) : []);
    setSaveError(null);
  };

  const closeEditModal = () => {
    setEditUser(null);
    setSaveError(null);
  };

  const toggleRole = (role: string) => {
    setEditRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const applyPreset = (presetName: string) => {
    setEditRoles([...(PRESETS[presetName] ?? [])]);
  };

  const handleSave = async () => {
    if (!editUser) return;

    if (requiresPartnerId(editRoles) && !editPartnerId.trim()) {
      setSaveError('Partner ID is required when assigning partner roles.');
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      const result = await client.updateUserClaims(editUser.uid, {
        partnerId: editPartnerId.trim(),
        roles: editRoles,
      });

      // Update the user in our local list
      setUsers((prev) =>
        prev.map((u) => (u.uid === editUser.uid ? { ...u, customClaims: result.customClaims } : u))
      );

      setSnackbar(result.message);
      closeEditModal();
    } catch (err) {
      console.error('[UserManagement] Failed to save claims:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save claims');
    } finally {
      setSaving(false);
    }
  };

  // Create user handlers
  const openCreateModal = () => {
    setCreateEmail('');
    setCreateFirstName('');
    setCreateLastName('');
    setCreatePartnerId('');
    setCreateRoles([]);
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateError(null);
  };

  const toggleCreateRole = (role: string) => {
    setCreateRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handleCreate = async () => {
    if (!createEmail.trim() || !createFirstName.trim() || !createLastName.trim()) {
      setCreateError('Email, first name, and last name are required.');
      return;
    }
    if (requiresPartnerId(createRoles) && !createPartnerId.trim()) {
      setCreateError('Partner ID is required when assigning partner roles.');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      const result = await client.createAdminUser({
        email: createEmail.trim(),
        firstName: createFirstName.trim(),
        lastName: createLastName.trim(),
        partnerId: createPartnerId.trim(),
        roles: createRoles,
      });
      setSnackbar(result.message);
      closeCreateModal();
      fetchUsers();
    } catch (err) {
      console.error('[UserManagement] Failed to create user:', err);
      setCreateError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // Disable/enable handler
  const handleToggleDisabled = async (user: AdminUser) => {
    try {
      setTogglingUid(user.uid);
      const result = await client.setAdminUserDisabled(user.uid, !user.disabled);
      setUsers((prev) =>
        prev.map((u) => (u.uid === user.uid ? { ...u, disabled: result.disabled } : u))
      );
      setSnackbar(result.message);
    } catch (err) {
      console.error('[UserManagement] Failed to toggle user status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setTogglingUid(null);
    }
  };

  // Remove handler
  const handleRemove = async () => {
    if (!removeUser) return;
    try {
      setRemoving(true);
      const result = await client.removeAdminUser(removeUser.uid);
      setSnackbar(result.message);
      setRemoveUser(null);
      fetchUsers();
    } catch (err) {
      console.error('[UserManagement] Failed to remove user:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove user');
      setRemoveUser(null);
    } finally {
      setRemoving(false);
    }
  };

  // Build live JSON preview
  const claimsPreview =
    editRoles.length > 0
      ? JSON.stringify(
          editPartnerId.trim()
            ? { partnerId: editPartnerId.trim(), roles: editRoles }
            : { roles: editRoles },
          null,
          2
        )
      : '{}';

  return (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Typography variant="h1" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage Firebase Auth users and their custom claims (roles &amp; partner ID)
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3 }}>
            <TextField
              size="small"
              placeholder="Search by email or name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-thin fa-magnifying-glass"
                      style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchInput('')}>
                      <i className="fa-thin fa-xmark" style={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <TextField
              select
              size="small"
              label="Partner"
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All partners</MenuItem>
              {partnerOptions.hasPartnerless && (
                <MenuItem value={NO_PARTNER}>No partner (internal)</MenuItem>
              )}
              {partnerOptions.options.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name ? `${p.name} (${p.id}${p.inactive ? ', inactive' : ''})` : p.id}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              size="small"
              onClick={openCreateModal}
              startIcon={<i className="fa-thin fa-user-plus" style={{ fontSize: 16 }} />}
              sx={{ height: 40 }}
            >
              Create User
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'email'}
                        direction={sortColumn === 'email' ? sortOrder : 'asc'}
                        onClick={() => handleSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'displayName'}
                        direction={sortColumn === 'displayName' ? sortOrder : 'asc'}
                        onClick={() => handleSort('displayName')}
                      >
                        Display Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'partner'}
                        direction={sortColumn === 'partner' ? sortOrder : 'asc'}
                        onClick={() => handleSort('partner')}
                      >
                        Partner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>
                      <Tooltip title="Most recent portal usage, based on the session's last token refresh (roughly hourly granularity).">
                        <TableSortLabel
                          active={sortColumn === 'lastActive'}
                          direction={sortColumn === 'lastActive' ? sortOrder : 'asc'}
                          onClick={() => handleSort('lastActive')}
                        >
                          Last Active
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : displayedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No users found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedUsers.map((u) => (
                      <TableRow key={u.uid} hover>
                        <TableCell>
                          <Typography variant="body2">{u.email ?? '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {u.uid}
                          </Typography>
                        </TableCell>
                        <TableCell>{u.displayName ?? '—'}</TableCell>
                        <TableCell>
                          {(() => {
                            const pid = getPartnerId(u);
                            if (pid === null) return '—';
                            const name = partnerNames.get(pid);
                            return (
                              <>
                                <Typography variant="body2">{name ?? pid}</Typography>
                                {name && (
                                  <Typography variant="caption" color="text.secondary">
                                    ID {pid}
                                    {inactivePartnerIds.has(pid) ? ' · inactive' : ''}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <RoleChips claims={u.customClaims} />
                        </TableCell>
                        <TableCell>{formatDate(u.lastRefreshTime)}</TableCell>
                        <TableCell>
                          <Chip
                            label={u.disabled ? 'Disabled' : 'Active'}
                            size="small"
                            sx={{
                              bgcolor: u.disabled ? '#F5F5F5' : '#E8F5E9',
                              color: u.disabled ? '#616161' : '#2E7D32',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" onClick={() => openEditModal(u)}>
                              Edit Claims
                            </Button>
                            <Tooltip title={u.disabled ? 'Re-enable account' : 'Disable account'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleDisabled(u)}
                                  disabled={togglingUid === u.uid}
                                >
                                  {togglingUid === u.uid ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <i
                                      className={
                                        u.disabled
                                          ? 'fa-thin fa-toggle-off'
                                          : 'fa-thin fa-toggle-on'
                                      }
                                      style={{ fontSize: 18 }}
                                    />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove user">
                              <IconButton
                                size="small"
                                onClick={() => setRemoveUser(u)}
                                sx={{ color: 'error.main' }}
                              >
                                <i className="fa-thin fa-trash" style={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {loading && users.length > 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Edit Claims Dialog */}
      <Dialog open={!!editUser} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Custom Claims
          <Typography variant="body2" color="text.secondary">
            {editUser?.email ?? editUser?.uid}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <TextField
            label="Partner ID"
            value={editPartnerId}
            onChange={(e) => setEditPartnerId(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 3 }}
            helperText="Required when assigning roles"
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Presets
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
            {Object.keys(PRESETS).map((name) => (
              <Button key={name} variant="outlined" size="small" onClick={() => applyPreset(name)}>
                {name}
              </Button>
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Roles
          </Typography>
          <FormGroup>
            {VALID_ROLES.map((role) => (
              <FormControlLabel
                key={role.key}
                control={
                  <Checkbox
                    checked={editRoles.includes(role.key)}
                    onChange={() => toggleRole(role.key)}
                    size="small"
                  />
                }
                label={role.label}
              />
            ))}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Claims Preview
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: '#F5F5F5',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre',
            }}
          >
            {claimsPreview}
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Claims'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={closeCreateModal} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                value={createFirstName}
                onChange={(e) => setCreateFirstName(e.target.value)}
                fullWidth
                size="small"
                required
              />
              <TextField
                label="Last Name"
                value={createLastName}
                onChange={(e) => setCreateLastName(e.target.value)}
                fullWidth
                size="small"
                required
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)}
              fullWidth
              size="small"
              required
            />
            <Alert severity="info">
              The user will receive an email invitation with a secure link to set their own
              password. The link expires after 48 hours.
            </Alert>
            <TextField
              label="Partner ID"
              value={createPartnerId}
              onChange={(e) => setCreatePartnerId(e.target.value)}
              fullWidth
              size="small"
              helperText="Required when assigning roles"
            />

            <Typography variant="subtitle2">Roles</Typography>
            <FormGroup>
              {VALID_ROLES.map((role) => (
                <FormControlLabel
                  key={role.key}
                  control={
                    <Checkbox
                      checked={createRoles.includes(role.key)}
                      onChange={() => toggleCreateRole(role.key)}
                      size="small"
                    />
                  }
                  label={role.label}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateModal} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove User Confirmation Dialog */}
      <Dialog open={!!removeUser} onClose={() => setRemoveUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{removeUser?.email ?? removeUser?.uid}</strong>?
            This will disable their account and revoke all access. This action can be reversed by
            re-enabling and re-assigning claims.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveUser(null)} disabled={removing}>
            Cancel
          </Button>
          <Button onClick={handleRemove} color="error" variant="contained" disabled={removing}>
            {removing ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </>
  );
}
